import React, { useState, useEffect } from 'react';
import { FileText, Search, Folder } from 'lucide-react';
import { TemplatesService } from '@/services';
import { TemplateResposta } from '@/types';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';

interface TemplateSelectorProps {
  ticketId: number;
  departamentoId?: number;
  onSelect: (conteudo: string) => void;
  onClose: () => void;
}

export default function TemplateSelector({
  ticketId,
  departamentoId,
  onSelect,
  onClose,
}: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<TemplateResposta[]>([]);
  const [categorias, setCategorias] = useState<string[]>([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [templateSelecionado, setTemplateSelecionado] = useState<TemplateResposta | null>(null);
  const [conteudoProcessado, setConteudoProcessado] = useState('');

  useEffect(() => {
    loadTemplates();
    loadCategorias();
  }, [departamentoId, categoriaFiltro]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await TemplatesService.getTemplates();
      setTemplates(data);
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategorias = async () => {
    try {
      // Categorias não estão disponíveis na API
      setCategorias([]);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleSelectTemplate = async (template: TemplateResposta) => {
    try {
      // Buscar o template e usar o conteúdo diretamente, sem processar variáveis
      const templateData = await TemplatesService.getTemplate(template.id);
      setTemplateSelecionado(template);
      setConteudoProcessado(templateData.conteudo || '');
    } catch (error: any) {
      console.error('Erro ao carregar template:', error);
      toast.error('Erro ao carregar template');
    }
  };

  const handleConfirm = () => {
    if (conteudoProcessado) {
      onSelect(conteudoProcessado);
      onClose();
    }
  };

  const templatesFiltrados = templates.filter(template =>
    template.nome.toLowerCase().includes(busca.toLowerCase()) ||
    template.conteudo.toLowerCase().includes(busca.toLowerCase())
  );

  const templatesPorCategoria = { 'Todos': templatesFiltrados };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="text-xl font-semibold text-gray-900 dark:text-white">
            Selecionar Template
          </span>
        </div>
      }
      maxWidth="2xl"
      className="flex flex-col max-h-[90vh]"
    >

        <div className="flex-1 overflow-hidden flex">
          {/* Sidebar com templates */}
          <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Filtros */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar template..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {categorias.length > 0 && (
                <select
                  value={categoriaFiltro}
                  onChange={(e) => setCategoriaFiltro(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Todas as categorias</option>
                  {categorias.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Lista de templates */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Carregando...
                </div>
              ) : templatesFiltrados.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nenhum template encontrado
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(templatesPorCategoria).map(([categoria, templatesCat]) => (
                    <div key={categoria} className="mb-4">
                      <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        <Folder className="w-4 h-4" />
                        {categoria}
                      </div>
                      {templatesCat.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            templateSelecionado?.id === template.id
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="font-medium text-gray-900 dark:text-white">
                            {template.nome}
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {templateSelecionado ? templateSelecionado.nome : 'Selecione um template'}
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {conteudoProcessado ? (
                <div
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: conteudoProcessado }}
                />
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Selecione um template para visualizar o conteúdo processado
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={!conteudoProcessado}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Usar Template
              </button>
            </div>
          </div>
        </div>
    </Modal>
  );
}

